import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { addBorehole, updateBorehole, getBoreholeDetails } from '../../../../redux/Slices/boreholesSlice';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { FaSave, FaArrowLeft, FaArrowRight, FaPlus, FaTrash, FaImages, FaCog } from 'react-icons/fa';
import axios from 'axios';
import { BASE_URL, getToken } from '../../../../shared/utils.js/utils';
import { getDominantColorFromImage } from '../../../../shared/utils.js/legendColor';

/* ─────────────────────── helpers ─────────────────────── */
const api = async (endpoint, params = {}) => {
  const token = getToken();
  const url = new URL(`${BASE_URL}${endpoint}`);
  Object.entries(params).forEach(([k, v]) => v !== undefined && v !== '' && url.searchParams.append(k, v));
  const res = await axios.get(url.toString(), { headers: { Authorization: `Bearer ${token}` } });
  return res.data;
};

const normalizePaginated = (payload) => {
  // Support two shapes:
  //   1. { data: { data: [], meta: {} } }  – standard Laravel paginator
  //   2. { data: [], current_page, last_page, total }  – contracts endpoint
  const raw = payload?.data ?? payload ?? {};
  const items = raw?.data ?? (Array.isArray(raw) ? raw : []);
  // pagination lives either in raw.meta or at raw root
  const hasMeta    = raw?.meta && typeof raw.meta === 'object';
  const hasRootPag = typeof raw.current_page !== 'undefined';
  const pagination = hasMeta ? {
    current_page: raw.meta.current_page ?? 1,
    last_page:    raw.meta.last_page    ?? 1,
    total:        raw.meta.total        ?? 0,
  } : hasRootPag ? {
    current_page: raw.current_page ?? 1,
    last_page:    raw.last_page    ?? 1,
    total:        raw.total        ?? 0,
  } : null;
  return { items: Array.isArray(items) ? items : [], pagination };
};

const normalizeId = (value) => {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'object') return value.id ?? '';
  return value;
};

const normalizeText = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') {
    return value.name_en || value.name_ar || value.name || value.title || value.code || '';
  }
  return String(value);
};

const getDisplayName = (item) => {
  if (!item) return '';
  if (typeof item === 'object') {
    return item.name_en || item.name_ar || item.name || item.title || item.code || '';
  }
  return String(item);
};

/* ─────────────────────── DropdownWithSearch ─────────────────────── */
const DropdownWithSearch = ({
  isOpen, label, items, pagination, searchTerm, onSearchChange,
  onPageChange, onSelect, onClose, isRTL, renderItem, placeholder, t,
}) => {
  const ref = useRef(null);
  useEffect(() => { if (isOpen) ref.current?.focus(); }, [isOpen]);
  if (!isOpen) return null;

  const current = pagination?.current_page ?? 1;
  const last    = pagination?.last_page    ?? 1;

  const paginationPages = () => {
    const pages = [];
    const start = Math.max(1, current - 2);
    const end   = Math.min(last, start + 4);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  return (
    <div style={ddMenuStyle} onClick={e => e.stopPropagation()}>
      <input
        ref={ref}
        placeholder={t('boreholes.dropdown.search', { label })}
        value={searchTerm}
        onChange={e => onSearchChange(e.target.value)}
        style={ddSearchStyle}
        autoFocus
      />
      <div style={{ maxHeight: 220, overflowY: 'auto' }}>
        {(Array.isArray(items) ? items : []).length === 0
          ? <div style={{ padding: 12, color: '#888', textAlign: 'center', fontSize: 13 }}>{t('boreholes.dropdown.noResults')}</div>
          : (Array.isArray(items) ? items : []).map(item => (
            <div key={item.id} style={ddItemStyle} onClick={() => { onSelect(item); onClose(); }}>
              {renderItem(item)}
            </div>
          ))
        }
      </div>
      {pagination && last > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 4, padding: '8px 0', borderTop: '1px solid #333', flexWrap: 'wrap' }}>
          <PagBtn disabled={current === 1} onClick={() => onPageChange(current - 1)}>‹</PagBtn>
          {paginationPages().map(p => (
            <PagBtn key={p} active={p === current} onClick={() => onPageChange(p)}>{p}</PagBtn>
          ))}
          <PagBtn disabled={current === last} onClick={() => onPageChange(current + 1)}>›</PagBtn>
        </div>
      )}
    </div>
  );
};

const PagBtn = ({ children, onClick, disabled, active }) => (
  <button
    type="button"
    onClick={e => { e.stopPropagation(); if (!disabled) onClick(); }}
    style={{
      padding: '3px 8px', border: '1px solid #444', borderRadius: 4, cursor: disabled ? 'not-allowed' : 'pointer',
      backgroundColor: active ? '#AC2000' : disabled ? '#2a2a2a' : '#1a1f2e',
      color: disabled ? '#666' : 'white', fontSize: 11, fontWeight: active ? 'bold' : 'normal',
    }}
  >{children}</button>
);

const SelectBox = ({ label, displayValue, onClick, required, disabled, isRTL, placeholder }) => (
  <div style={{ marginBottom: 16 }}>
    <label style={labelStyle}>{label}{required && <span style={{ color: '#AC2000' }}> *</span>}</label>
    <div
      onClick={!disabled ? onClick : undefined}
      style={{
        ...inputStyle, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1, direction: isRTL ? 'rtl' : 'ltr',
      }}
    >
      <span style={{ color: displayValue ? 'white' : '#666', fontSize: 14 }}>{displayValue || placeholder || label}</span>
      <span style={{ fontSize: 10, color: '#888' }}>▼</span>
    </div>
  </div>
);

/* ─────────────────────── Layer row ─────────────────────── */
const emptyLayer = () => ({
  id: undefined,
  start_depth: '', end_depth: '', layer_description: '', legend_id: '',
  spt_n: '', rqd_perc: '', tcr_perc: '', ucs_pn: '',
});

const getLayerFields = (t) => [
  { key: 'start_depth',      label: t('boreholes.layers.from'), type: 'depth' },
  { key: 'end_depth',        label: t('boreholes.layers.to'), type: 'depth' },
  { key: 'layer_description',label: t('boreholes.layers.description'), type: 'text' },
  { key: 'legend_id',        label: t('boreholes.layers.legend'), type: 'legend' },
  { key: 'spt_n',            label: 'SPT-N', type: 'multi-number' },
  { key: 'rqd_perc',         label: 'RQD%', type: 'multi-number' },
  { key: 'tcr_perc',         label: 'TCR%', type: 'multi-number' },
  { key: 'ucs_pn',           label: 'UCS', type: 'multi-number' },
];

// الحقول اللي ممكن تاخد أكتر من قراءة لنفس الطبقة (SPT-N / TCR / UCS) بيتم تخزينها كنص مفصول بفاصلة "12, 15"
const parseMultiEntries = (value) => {
  const str = value === undefined || value === null ? '' : String(value);
  if (!str.trim()) return [''];
  return str.split(',').map(s => s.trim());
};

// بيرجع النص النهائي جاهز للإرسال: بيشيل القيم الفاضية بس بيحافظ على القيم اللي المستخدم لسه بيكتب فيها
const cleanMultiValue = (value) => {
  const cleaned = parseMultiEntries(value).filter(Boolean).join(', ');
  return cleaned || undefined;
};

const MultiValueField = ({ value, onChange }) => {
  const entries = parseMultiEntries(value);
  const updateEntry = (i, v) => {
    const next = [...entries];
    next[i] = v;
    onChange(next.join(', '));
  };
  const addEntry = () => onChange([...entries, ''].join(', '));
  const removeEntry = (i) => onChange(entries.filter((_, idx) => idx !== i).join(', '));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {entries.map((entry, i) => (
        <div key={i} style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          <input
            type="text"
            value={entry}
            onChange={e => updateEntry(i, e.target.value)}
            style={{ ...layerInputStyle, width: 'auto', flex: 1 }}
          />
          {entries.length > 1 && (
            <button
              type="button"
              onClick={() => removeEntry(i)}
              style={{ ...removeBtnStyle, padding: '3px 7px', fontSize: 11 }}
            >
              ×
            </button>
          )}
        </div>
      ))}
      <button
        type="button"
        onClick={addEntry}
        style={{
          background: 'none', border: '1px dashed #4a6fa5', color: '#9db3d1',
          borderRadius: 4, padding: '3px 6px', fontSize: 11, cursor: 'pointer',
        }}
      >
        <FaPlus size={9} />
      </button>
    </div>
  );
};

/* ─────────────────────── Depth ruler (القطاع الهندسي) ─────────────────────── */
const MAX_BOREHOLE_DEPTH = 50; // أقصى عمق ممكن نوصله في الجسات

const getDepthTickStep = (depth) => {
  if (depth <= 15) return 1;
  return 2;
};

// يمنع اختيار "من" أصغر من نهاية الطبقة اللي قبلها، و"إلى" أصغر من أو تساوي "من" في نفس الطبقة
const getMinDepthForField = (fieldKey, idx, layer, layers) => {
  if (fieldKey === 'start_depth' && idx > 0) {
    const prevEnd = Number(layers[idx - 1].end_depth);
    if (Number.isFinite(prevEnd)) return prevEnd;
  }
  if (fieldKey === 'end_depth') {
    const ownStart = Number(layer.start_depth);
    if (Number.isFinite(ownStart)) return ownStart + 1;
  }
  return 0;
};

// نفس منطق تلوين الطبقات المستخدم في تقرير الجسة المطبوع (لو الاسم اتعرف عليه)
const getKnownLayerColor = (legend) => {
  const lg = legend ? legend.toLowerCase() : '';
  if (lg.includes('sand') || lg.includes('رمل')) return '#c2b280';
  if (lg.includes('clay') || lg.includes('طين')) return '#6e5a4e';
  if (lg.includes('rock') || lg.includes('صخر')) return '#888c8d';
  if (lg.includes('silt') || lg.includes('طمي')) return '#a59371';
  if (lg.includes('gravel') || lg.includes('زلط') || lg.includes('حصى')) return '#9aa0a6';
  return null;
};

// لو الاسم مش من الأسماء الجيولوجية المعروفة، بنولّد لون مميز وثابت لكل رمز (نفس الرمز = نفس اللون دايمًا)
const getLegendHashColor = (legendId) => {
  const str = String(legendId ?? '');
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) hash = (hash * 31 + str.charCodeAt(i)) % 360;
  const hue = str ? hash : 0;
  return `hsl(${hue}, 62%, 55%)`;
};

const getLayerColor = (legendId, legendText) => {
  const known = getKnownLayerColor(legendText);
  if (known) return known;
  if (legendId) return getLegendHashColor(legendId);
  return '#8a7d65'; // لا يوجد رمز محدد
};

const DepthRuler = ({ totalDepth, layers, t, getLegendById, legendColors = {} }) => {
  const rawDepth = Number(totalDepth) || 0;
  const effectiveMax = Math.min(Math.max(rawDepth, 1), MAX_BOREHOLE_DEPTH);
  const step = getDepthTickStep(effectiveMax);

  const ticks = [];
  for (let d = 0; d <= effectiveMax; d += step) ticks.push(Math.round(d * 100) / 100);
  if (ticks[ticks.length - 1] !== effectiveMax) ticks.push(effectiveMax);

  const layerBands = layers
    .map((layer, idx) => {
      const start = Number(layer.start_depth);
      const end = Number(layer.end_depth);
      if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return null;
      const clampedStart = Math.min(Math.max(start, 0), effectiveMax);
      const clampedEnd = Math.min(Math.max(end, 0), effectiveMax);
      if (clampedEnd <= clampedStart) return null;
      const legend = getLegendById ? getLegendById(layer.legend_id) : null;
      const legendText = legend?.label_ar || legend?.label_en || layer.layer_description || '';
      // اللون الحقيقي المستخرج من صورة الرمز نفسه بياخد الأولوية، ولو لسه بيتحمّل بنرجع للتخمين القديم مؤقتًا
      const preciseColor = layer.legend_id ? legendColors[String(layer.legend_id)] : null;
      return {
        idx,
        top: (clampedStart / effectiveMax) * 100,
        height: ((clampedEnd - clampedStart) / effectiveMax) * 100,
        color: preciseColor || getLayerColor(layer.legend_id, legendText),
      };
    })
    .filter(Boolean);

  const exceedsMax = rawDepth > MAX_BOREHOLE_DEPTH;
  const minHeight = Math.max(220, ticks.length * 22);

  return (
    <div style={{ position: 'relative', width: '100%', minHeight, height: '100%' }}>
      {layerBands.map((band) => (
        <div
          key={band.idx}
          title={`${layers[band.idx].start_depth}–${layers[band.idx].end_depth} م`}
          style={{
            position: 'absolute',
            top: `${band.top}%`,
            height: `${band.height}%`,
            insetInlineStart: 4,
            width: 14,
            background: band.color,
            opacity: 0.85,
            borderRadius: 3,
          }}
        />
      ))}
      {ticks.map((d) => {
        const topPct = (d / effectiveMax) * 100;
        return (
          <div
            key={d}
            style={{
              position: 'absolute',
              top: `${topPct}%`,
              insetInlineStart: 22,
              insetInlineEnd: 0,
              transform: 'translateY(-50%)',
              display: 'flex',
              alignItems: 'center',
              gap: 3,
              whiteSpace: 'nowrap',
            }}
          >
            <span style={{ width: 10, height: 1, background: '#555' }} />
            <span style={{ fontSize: 11, color: '#ccc' }}>{d}م</span>
          </div>
        );
      })}
      {exceedsMax && (
        <span style={{ position: 'absolute', bottom: -18, insetInlineStart: 0, insetInlineEnd: 0, fontSize: 10, color: '#F6630d', textAlign: 'center' }}>
          {t('boreholes.ruler_max_depth_note', { defaultValue: `أقصى عمق ${MAX_BOREHOLE_DEPTH}م` })}
        </span>
      )}
    </div>
  );
};

/* ═══════════════════════ MAIN COMPONENT ═══════════════════════ */
const AddBorehole = () => {
  const { id } = useParams();
  const isEditMode = !!id;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isLoading, details } = useSelector(s => s.boreholes);
  const { t, i18n } = useTranslation('global');
  const isRTL = i18n.language === 'ar';
  const dir   = isRTL ? 'rtl' : 'ltr';
  const LAYER_FIELDS = getLayerFields(t);

  /* ── Form state ── */
  const [formData, setFormData] = useState({
    customer_id: '', project_id: '',
    location: '', consultant: '', boring_method: '', diameter: '',
    groundwater_level: '', ace_ref_no: '', report_date: '',
    total_depth: '', borehole_number: '',
  });
  const [layers, setLayers] = useState([emptyLayer()]);
  const depthSelectMax = Math.min(Math.max(Math.ceil(Number(formData.total_depth) || 0), 1), MAX_BOREHOLE_DEPTH);
  const depthOptions = Array.from({ length: depthSelectMax + 1 }, (_, i) => i);
  const [legends, setLegends] = useState([]);
  const [allLegends, setAllLegends] = useState([]);
  const [isLegendsLoading, setIsLegendsLoading] = useState(false);
  const [showLegendLibrary, setShowLegendLibrary] = useState(false);
  const [newLegendForm, setNewLegendForm] = useState({
    label_en: '',
    label_ar: '',
    code: '',
    sort_order: '',
    is_active: true,
    image: null,
  });
  const [editLegendForm, setEditLegendForm] = useState(null);
  const [legendPickerRowIndex, setLegendPickerRowIndex] = useState(null);

  /* ── Customer dropdown ── */
  const [custOpen, setCustOpen]           = useState(false);
  const [custItems, setCustItems]         = useState([]);
  const [custPage, setCustPage]           = useState(1);
  const [custPagination, setCustPagination] = useState(null);
  const [custSearch, setCustSearch]       = useState('');
  const [selectedCust, setSelectedCust]   = useState(null);

  /* ── Project dropdown ── */
  const [projOpen, setProjOpen]           = useState(false);
  const [projItems, setProjItems]         = useState([]);
  const [projPage, setProjPage]           = useState(1);
  const [projPagination, setProjPagination] = useState(null);
  const [projSearch, setProjSearch]       = useState('');
  const [selectedProj, setSelectedProj]   = useState(null);

  /* ── Contract dropdown ── */
  const [contractOpen, setContractOpen]             = useState(false);
  const [contractItems, setContractItems]           = useState([]);
  const [contractPage, setContractPage]             = useState(1);
  const [contractPagination, setContractPagination] = useState(null);
  const [contractSearch, setContractSearch]         = useState('');
  const [selectedContract, setSelectedContract]     = useState(null);

  /* ── Contractor dropdown (uses /customers endpoint) ── */
  const [contrOpen, setContrOpen]             = useState(false);
  const [contrItems, setContrItems]           = useState([]);
  const [contrPage, setContrPage]             = useState(1);
  const [contrPagination, setContrPagination] = useState(null);
  const [contrSearch, setContrSearch]         = useState('');
  const [selectedContr, setSelectedContr]     = useState(null);

  /* ── Close on outside click ── */
  useEffect(() => {
    const handler = e => {
      if (!e.target.closest('[data-dd]')) {
        setCustOpen(false); setProjOpen(false); setContractOpen(false); setContrOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const fetchLegends = useCallback(async () => {
    setIsLegendsLoading(true);
    try {
      const token = getToken();
      const response = await axios.get(`${BASE_URL}/legends`, { headers: { Authorization: `Bearer ${token}` } });
      const payload = response.data?.data ?? response.data ?? [];
      const list = Array.isArray(payload) ? payload : [];
      setAllLegends(list);
      setLegends(list.filter((item) => item?.is_active));
    } catch (err) {
      toast.error(t('boreholes.toasts.fetchLegendsError', { defaultValue: 'تعذر تحميل مكتبة الرموز' }));
    } finally {
      setIsLegendsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchLegends();
  }, [fetchLegends]);

  useEffect(() => {
    if (showLegendLibrary) fetchLegends();
  }, [showLegendLibrary, fetchLegends]);

  /* ── Fetch customers ── */
  const fetchCustomers = useCallback(async (page = 1, search = '') => {
    try {
      const data = await api('/customers', { per_page: 10, page, search: search || undefined });
      const { items, pagination } = normalizePaginated(data);
      setCustItems(items); setCustPagination(pagination);
    } catch (err) { console.error('fetch customers', err); }
  }, []);

  useEffect(() => {
    if (!custOpen) return;
    const t = setTimeout(() => fetchCustomers(custPage, custSearch), 300);
    return () => clearTimeout(t);
  }, [custOpen, custPage, custSearch, fetchCustomers]);

  /* ── Fetch projects (filtered by customer_id) ── */
  const fetchProjects = useCallback(async (page = 1, search = '') => {
    if (!selectedCust) { setProjItems([]); return; }
    try {
      const data = await api('/projects', { per_page: 10, page, customer_id: selectedCust.id, search: search || undefined });
      const { items, pagination } = normalizePaginated(data);
      setProjItems(items); setProjPagination(pagination);
    } catch (err) { console.error('fetch projects', err); }
  }, [selectedCust]);

  useEffect(() => {
    if (!projOpen) return;
    const t = setTimeout(() => fetchProjects(projPage, projSearch), 300);
    return () => clearTimeout(t);
  }, [projOpen, projPage, projSearch, fetchProjects]);

  
  /* ── Fetch contractors (use customers endpoint as contractors) ── */
  const fetchContractors = useCallback(async (page = 1, search = '') => {
    try {
      const data = await api('/customers', { per_page: 10, page, search: search || undefined });
      const { items, pagination } = normalizePaginated(data);
      setContrItems(items); setContrPagination(pagination);
    } catch (err) { console.error('fetch contractors', err); }
  }, []);

  useEffect(() => {
    if (!contrOpen) return;
    const t = setTimeout(() => fetchContractors(contrPage, contrSearch), 300);
    return () => clearTimeout(t);
  }, [contrOpen, contrPage, contrSearch, fetchContractors]);

  /* ── Load for edit ── */
  useEffect(() => { if (isEditMode) dispatch(getBoreholeDetails(id)); }, [dispatch, id, isEditMode]);
  useEffect(() => {
    if (isEditMode && details) {
      setFormData({
        customer_id:       normalizeId(details.customer_id ?? details.customer),
        project_id:        normalizeId(details.project_id ?? details.project),
        contract_id:       normalizeId(details.contract_id ?? details.contract),
        location:          normalizeText(details.location),
        consultant:        normalizeText(details.consultant),
        boring_method:     normalizeText(details.boring_method),
        diameter:          normalizeText(details.diameter),
        groundwater_level: normalizeText(details.groundwater_level),
        ace_ref_no:        normalizeText(details.ace_ref_no),
        report_date:       normalizeText(details.report_date),
        total_depth:       normalizeText(details.total_depth),
        borehole_number:   normalizeText(details.borehole_number),
      });
      if (details.layers?.length) setLayers(details.layers.map(l => ({
        id: l.id,
        start_depth: l.start_depth ?? '', end_depth: l.end_depth ?? '',
        layer_description: l.layer_description ?? '', legend_id: normalizeId(l.legend_id ?? l.legend),
        spt_n: l.spt_n ?? '', rqd_perc: l.rqd_perc ?? '',
        tcr_perc: l.tcr_perc ?? '', ucs_pn: l.ucs_pn ?? '',
      })));
    }
  }, [isEditMode, details]);

  const fetchCustomerById = useCallback(async (customerId) => {
    if (!customerId) return null;
    try {
      const token = getToken();
      const response = await axios.get(`${BASE_URL}/customers/${customerId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response?.data?.data || response?.data || null;
    } catch (err) {
      return null;
    }
  }, []);

  // Sync selection objects for UI display in Edit Mode
  useEffect(() => {
    if (!isEditMode || !details) return;
    if ((details.owner || details.customer) && !selectedCust) setSelectedCust(details.owner || details.customer);
    if (details.project && !selectedProj) setSelectedProj(details.project);
    if (details.contractor && !selectedContr) setSelectedContr(details.contractor);
  }, [isEditMode, details, selectedCust, selectedProj, selectedContr]);

  // Fallback: sometimes details include only customer_id without the relation object.
  useEffect(() => {
    let cancelled = false;
    const syncCustomerFallback = async () => {
      if (!isEditMode || selectedCust) return;
      const customerId = formData.customer_id || details?.customer_id;
      if (!customerId) return;
      const customer = await fetchCustomerById(customerId);
      if (!cancelled && customer) {
        setSelectedCust(customer);
      }
    };
    syncCustomerFallback();
    return () => {
      cancelled = true;
    };
  }, [isEditMode, selectedCust, formData.customer_id, details?.customer_id, fetchCustomerById]);

  const customerDisplayValue =
    getDisplayName(selectedCust) ||
    getDisplayName(details?.owner || details?.customer) ||
    '';

  useEffect(() => {
    if (isEditMode && details && customerDisplayValue) {
      setCustItems((prev) => {
        if (prev.some((item) => String(item?.id) === String(selectedCust?.id))) return prev;
        if (selectedCust && typeof selectedCust === 'object') return [selectedCust, ...prev];
        return prev;
      });
    }
  }, [isEditMode, details, customerDisplayValue, selectedCust]);

  /* ── Change handlers ── */
  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleLayerChange = (idx, field, value) => {
    setLayers(prev => prev.map((l, i) => i === idx ? { ...l, [field]: value } : l));
  };

  const addLayer = () => setLayers(prev => {
    const lastLayer = prev[prev.length - 1];
    const newLayer = emptyLayer();
    if (lastLayer && lastLayer.end_depth !== '' && lastLayer.end_depth !== undefined && lastLayer.end_depth !== null) {
      newLayer.start_depth = lastLayer.end_depth;
    }
    return [...prev, newLayer];
  });
  const removeLayer = idx => setLayers(prev => prev.filter((_, i) => i !== idx));

  /* ── Submit ── */
  const handleSave = async e => {
    e.preventDefault();

    if (!formData.borehole_number) {
      toast.error(t('boreholes.fields.boreholeNumber') + ' ' + t('common.required', { defaultValue: 'مطلوب' }));
      return;
    }
    if (!formData.report_date) {
      toast.error(t('boreholes.fields.reportDate') + ' ' + t('common.required', { defaultValue: 'مطلوب' }));
      return;
    }

    const payload = {
      ...Object.fromEntries(Object.entries(formData).filter(([, v]) => v !== '')),
      layers: layers
        .filter(l => l.start_depth !== '' || l.end_depth !== '')
        .map(l => ({
          ...(l.id ? { id: Number(l.id) } : {}),
          start_depth:       l.start_depth      !== '' ? Number(l.start_depth)      : undefined,
          end_depth:         l.end_depth        !== '' ? Number(l.end_depth)        : undefined,
          layer_description: l.layer_description || undefined,
          legend_id:         l.legend_id         !== '' ? Number(l.legend_id)        : undefined,
          spt_n:             cleanMultiValue(l.spt_n),
          rqd_perc:          cleanMultiValue(l.rqd_perc) ?? null,
          tcr_perc:          cleanMultiValue(l.tcr_perc) ?? null,
          ucs_pn:            cleanMultiValue(l.ucs_pn)   ?? null,
        })),
    };

    try {
      if (isEditMode) {
        await dispatch(updateBorehole({ id, boreholeData: { ...payload, _method: 'PUT' } })).unwrap();
      } else {
        await dispatch(addBorehole(payload)).unwrap();
      }
      toast.success(isEditMode ? t('boreholes.toasts.editSuccess') : t('boreholes.toasts.addSuccess'));
      navigate('/boreholes');
    } catch (err) {
      const msg =
        (typeof err === 'string' && err) ||
        err?.message ||
        err?.data?.message ||
        err?.response?.data?.message ||
        err?.name_en ||
        err?.name_ar ||
        err?.name ||
        t('boreholes.toasts.error');
      toast.error(String(msg));
    }
  };

  const createLegend = async () => {
    if (!newLegendForm.label_en || !newLegendForm.image) {
      toast.warn(t('boreholes.toasts.legendRequired', { defaultValue: 'الاسم الإنجليزي والصورة مطلوبان' }));
      return;
    }
    try {
      const token = getToken();
      const fd = new FormData();
      fd.append('label_en', newLegendForm.label_en);
      if (newLegendForm.label_ar) fd.append('label_ar', newLegendForm.label_ar);
      if (newLegendForm.code) fd.append('code', newLegendForm.code);
      if (newLegendForm.sort_order !== '') fd.append('sort_order', newLegendForm.sort_order);
      fd.append('is_active', newLegendForm.is_active ? '1' : '0');
      fd.append('image', newLegendForm.image);
      await axios.post(`${BASE_URL}/legends`, fd, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t('boreholes.toasts.legendCreated', { defaultValue: 'تمت إضافة الرمز بنجاح' }));
      setNewLegendForm({ label_en: '', label_ar: '', code: '', sort_order: '', is_active: true, image: null });
      fetchLegends();
    } catch (err) {
      toast.error(err?.response?.data?.message || t('boreholes.toasts.legendCreateError', { defaultValue: 'تعذر إضافة الرمز' }));
    }
  };

  const updateLegend = async (legendId, updates) => {
    try {
      const token = getToken();
      const fd = new FormData();
      fd.append('label_en', updates.label_en || '');
      fd.append('label_ar', updates.label_ar || '');
      fd.append('code', updates.code || '');
      fd.append('sort_order', updates.sort_order !== '' ? String(updates.sort_order) : '0');
      fd.append('is_active', updates.is_active ? '1' : '0');
      if (updates.image) {
        fd.append('image', updates.image);
      }

      await axios.patch(`${BASE_URL}/legends/${legendId}`, fd, { headers: { Authorization: `Bearer ${token}` } });
      setEditLegendForm(null);
      fetchLegends();
    } catch (err) {
      toast.error(err?.response?.data?.message || t('boreholes.toasts.legendUpdateError', { defaultValue: 'تعذر تعديل الرمز' }));
    }
  };

  const legendImageUrl = (legend) => {
    if (!legend?.id) return null;
    // مسار التخزين المباشر (public/storage) مش متظبط على السيرفر، فبنستخدم الراوت المخصص لعرض صورة الرمز
    if (legend.image_path && /^https?:\/\//i.test(legend.image_path)) return legend.image_path;
    return `${BASE_URL}/legends/${legend.id}/image`;
  };

  const getLegendById = (legendId) => allLegends.find((item) => String(item.id) === String(legendId));

  // بنستخرج اللون الحقيقي لكل رمز مستخدم في الطبقات من صورته نفسها، عشان لون المسطرة يطابق الرمز بالظبط
  const [legendColors, setLegendColors] = useState({});
  useEffect(() => {
    let cancelled = false;
    const usedLegendIds = Array.from(
      new Set(layers.map((l) => l.legend_id).filter(Boolean).map(String)),
    );
    usedLegendIds.forEach((id) => {
      if (legendColors[id]) return;
      const url = legendImageUrl(getLegendById(id));
      if (!url) return;
      getDominantColorFromImage(url).then((color) => {
        if (cancelled || !color) return;
        setLegendColors((prev) => (prev[id] ? prev : { ...prev, [id]: color }));
      });
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layers, allLegends]);
  const sidebarSafeOverlayStyle = isRTL
    ? { ...modalOverlayStyle, right: 310, left: 0 }
    : { ...modalOverlayStyle, left: 310, right: 0 };

  /* ─────────── Render ─────────── */
  return (
    <div style={{ padding: 24, backgroundColor: '#14263b', minHeight: 'calc(100vh - 80px)', color: 'white', direction: dir }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <button onClick={() => navigate('/boreholes')} style={backBtnStyle}>
          {isRTL ? <FaArrowRight /> : <FaArrowLeft />}
          <span>{t('boreholes.back')}</span>
        </button>
        <h1 style={{ fontSize: 22, fontWeight: 'bold', margin: 0 }}>
          {isEditMode ? t('boreholes.pageTitle.edit') : t('boreholes.pageTitle.add')}
        </h1>
        <div style={{ width: 80 }} />
      </div>

      <form onSubmit={handleSave}>
        {/* ── Section: Parties ── */}
        <Section title={t('boreholes.sections.parties')}>
          <div style={gridStyle}>
            {/* Customer */}
            <div style={{ position: 'relative' }} data-dd>
              <SelectBox
                label={t('boreholes.fields.customerOrContractor')}
                placeholder={t('boreholes.dropdown.select', { label: t('boreholes.fields.customerOrContractor') })}
                displayValue={customerDisplayValue}
                onClick={() => { setCustOpen(o => !o); setProjOpen(false); setContrOpen(false); }}
                isRTL={isRTL}
              />
              <DropdownWithSearch
                isOpen={custOpen} label={t('boreholes.fields.customer')} items={custItems} pagination={custPagination}
                searchTerm={custSearch} onSearchChange={v => { setCustSearch(v); setCustPage(1); }}
                onPageChange={p => setCustPage(p)}
                onSelect={item => {
                  setSelectedCust(item);
                  setFormData(p => ({ ...p, customer_id: item.id, project_id: '', contract_id: '' }));
                  setSelectedProj(null); setProjItems([]); setProjPage(1);
                  setSelectedContract(null); setContractItems([]); setContractPage(1);
                }}
                onClose={() => setCustOpen(false)}
                renderItem={item => <span>{ item.name ||item.name_en || item.name_ar || item.id} <small style={{ color: '#888' }}>({item.code})</small></span>}
                isRTL={isRTL}
                t={t}
              />
            </div>

            {/* Project */}
            <div style={{ position: 'relative' }} data-dd>
              <SelectBox
                label={t('boreholes.fields.project')}
                placeholder={t('boreholes.dropdown.select', { label: t('boreholes.fields.project') })}
                displayValue={getDisplayName(selectedProj)}
                onClick={() => { if (!selectedCust && !isEditMode) { toast.warn(t('boreholes.toasts.selectCustomerFirst')); return; } setProjOpen(o => !o); setCustOpen(false); setContrOpen(false); }}
                disabled={!selectedCust && !isEditMode && !formData.project_id}
                isRTL={isRTL}
              />
              <DropdownWithSearch
                isOpen={projOpen} label={t('boreholes.fields.project')} items={projItems} pagination={projPagination}
                searchTerm={projSearch} onSearchChange={v => { setProjSearch(v); setProjPage(1); }}
                onPageChange={p => setProjPage(p)}
                onSelect={item => {
                  setSelectedProj(item);
                  setFormData(p => ({ ...p, project_id: item.id, contract_id: '' }));
                  setSelectedContract(null); setContractItems([]); setContractPage(1);
                }}
                onClose={() => setProjOpen(false)}
                renderItem={item => <span>{item.name_en || item.name_ar || item.name || item.id} <small style={{ color: '#888' }}>({item.code})</small></span>}
                isRTL={isRTL}
                t={t}
              />
            </div>

            {/* 
            <div style={{ position: 'relative' }} data-dd>
              <SelectBox
                label="العقد (Contract)"
                displayValue={
                  selectedContract
                    ? `${selectedContract.code} – ${(selectedContract.customer?.name || selectedContract.customer?.name_ar || '')}`
                    : (formData.contract_id ? `ID: ${formData.contract_id}` : '')
                }
                onClick={() => {
                  if (!selectedCust && !isEditMode) { toast.warn('اختر العميل أولاً'); return; }
                  setContractOpen(o => !o); setCustOpen(false); setProjOpen(false); setContrOpen(false);
                }}
                disabled={!selectedCust && !isEditMode && !formData.contract_id}
                isRTL={isRTL}
              />
              <DropdownWithSearch
                isOpen={contractOpen} label="العقد" items={contractItems} pagination={contractPagination}
                searchTerm={contractSearch} onSearchChange={v => { setContractSearch(v); setContractPage(1); }}
                onPageChange={p => setContractPage(p)}
                onSelect={item => {
                  setSelectedContract(item);
                  setFormData(p => ({ ...p, contract_id: item.id }));
                }}
                onClose={() => setContractOpen(false)}
                renderItem={item => (
                  <span>
                    <strong>{item.code}</strong>
                    <small style={{ color: '#888', marginRight: 6 }}>
                      {item.project?.name || item.project?.name_ar || ''}
                    </small>
                    <small style={{ color: '#aaa', marginRight: 6 }}>
                      {Number(item.total_amount).toLocaleString()} {item.currency?.symbol || ''}
                    </small>
                  </span>
                )}
                isRTL={isRTL}
              />
            </div>
*/}
            {/* Contractor */}
            {/* <div style={{ position: 'relative' }} data-dd>
              <SelectBox
                label={t('boreholes.fields.contractor')}
                placeholder={t('boreholes.dropdown.select', { label: t('boreholes.fields.contractor') })}
                displayValue={selectedContr ? (selectedContr.name || selectedContr.name_ar) : (formData.contractor_id ? `ID: ${formData.contractor_id}` : '')}
                onClick={() => { setContrOpen(o => !o); setCustOpen(false); setProjOpen(false); setContractOpen(false); }}
                isRTL={isRTL}
              />
              <DropdownWithSearch
                isOpen={contrOpen} label={t('boreholes.fields.contractor')} items={contrItems} pagination={contrPagination}
                searchTerm={contrSearch} onSearchChange={v => { setContrSearch(v); setContrPage(1); }}
                onPageChange={p => setContrPage(p)}
                onSelect={item => {
                  setSelectedContr(item); setFormData(p => ({ ...p, contractor_id: item.id }));
                }}
                onClose={() => setContrOpen(false)}
                renderItem={item => <span>{item.name || item.name_ar} <small style={{ color: '#888' }}>({item.code})</small></span>}
                isRTL={isRTL}
                t={t}
              />
            </div> */}
          </div>
        </Section>

        {/* ── Section: General Info ── */}
        <Section title={t('boreholes.sections.details')}>
          <div style={gridStyle}>
            <Field label={t('boreholes.fields.boreholeNumber')} name="borehole_number"   value={formData.borehole_number}   onChange={handleChange} required />
            <Field label={t('boreholes.fields.reportDate')}    name="report_date"       value={formData.report_date}       onChange={handleChange} type="date" required />
            <Field label={t('boreholes.fields.totalDepth')}    name="total_depth"       value={formData.total_depth}       onChange={handleChange} type="number" step="0.01" min="0" />
            <Field label={t('boreholes.fields.location')}      name="location"          value={formData.location}          onChange={handleChange} />
            <Field label={t('boreholes.fields.consultant')}    name="consultant"        value={formData.consultant}        onChange={handleChange} />
            <Field label={t('boreholes.fields.boringMethod')}  name="boring_method"     value={formData.boring_method}     onChange={handleChange} />
            <Field label={t('boreholes.fields.diameter')}      name="diameter"          value={formData.diameter}          onChange={handleChange} />
            <Field label={t('boreholes.fields.groundwaterLevel')} name="groundwater_level" value={formData.groundwater_level} onChange={handleChange} />
            <Field label={t('boreholes.fields.aceRefNo')}      name="ace_ref_no"        value={formData.ace_ref_no}        onChange={handleChange} />
          </div>
        </Section>

        {/* ── Section: Layers ── */}
        <Section title={t('boreholes.sections.layers')}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <small style={{ color: '#99a3b3' }}>
              {t('boreholes.legendNote', { defaultValue: 'يتم الإرسال بـ legend_id فقط.' })}
            </small>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" onClick={() => setShowLegendLibrary(true)} style={secondaryBtnStyle}>
                <FaImages /> {t('boreholes.legendLibrary', { defaultValue: 'مكتبة الرموز' })}
              </button>
              <button type="button" onClick={() => navigate('/boreholes/legends')} style={secondaryBtnStyle}>
                <FaImages /> {t('boreholes.legendCrud', { defaultValue: 'إدارة الرموز' })}
              </button>
              <button type="button" onClick={() => navigate('/boreholes/defaults')} style={secondaryBtnStyle}>
                <FaCog /> {t('boreholes.defaults', { defaultValue: 'الإعدادات الافتراضية للجسات' })}
              </button>
            </div>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 800 }}>
              <thead>
                <tr style={{ backgroundColor: '#AC2000' }}>
                  <th style={thStyle}>{isRTL ? 'عمق الجسة' : 'Depth'}</th>
                  {LAYER_FIELDS.map(f => <th key={f.key} style={thStyle}>{f.label}</th>)}
                  <th style={thStyle}>{t('boreholes.layers.delete')}</th>
                </tr>
              </thead>
              <tbody>
                {layers.map((layer, idx) => (
                  <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? '#1a2133' : '#1e2740' }}>
                    {idx === 0 && (
                      <td rowSpan={layers.length} style={{ padding: '6px 4px', borderBottom: '1px solid #333', borderInlineEnd: '1px solid #333', verticalAlign: 'top' }}>
                        <DepthRuler totalDepth={formData.total_depth} layers={layers} t={t} getLegendById={getLegendById} legendColors={legendColors} />
                      </td>
                    )}
                    {LAYER_FIELDS.map(f => (
                      <td key={f.key} style={{ padding: '4px', borderBottom: '1px solid #333' }}>
                        {f.type === 'legend' ? (
                          <button
                            type="button"
                            onClick={() => setLegendPickerRowIndex(idx)}
                            style={{ ...layerInputStyle, display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between' }}
                          >
                            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              {getLegendById(layer[f.key]) && legendImageUrl(getLegendById(layer[f.key])) ? (
                                <img
                                  src={legendImageUrl(getLegendById(layer[f.key]))}
                                  alt="legend"
                                  style={{ width: 30, height: 30, objectFit: 'contain', background: '#fff', borderRadius: 4 }}
                                />
                              ) : null}
                              <span>
                                {getLegendById(layer[f.key])
                                  ? (getLegendById(layer[f.key])?.label_ar || getLegendById(layer[f.key])?.label_en || getLegendById(layer[f.key])?.code || `#${layer[f.key]}`)
                                  : t('boreholes.selectLegend', { defaultValue: 'اختر الرمز' })}
                              </span>
                            </span>
                            <span style={{ color: '#888' }}>▼</span>
                          </button>
                        ) : f.type === 'depth' ? (
                          <select
                            value={layer[f.key]}
                            onChange={e => handleLayerChange(idx, f.key, e.target.value)}
                            style={layerInputStyle}
                          >
                            <option value="">—</option>
                            {depthOptions
                              .filter(d => d >= getMinDepthForField(f.key, idx, layer, layers))
                              .map(d => (
                                <option key={d} value={d}>{d}</option>
                              ))}
                          </select>
                        ) : f.type === 'multi-number' ? (
                          <MultiValueField
                            value={layer[f.key]}
                            onChange={val => handleLayerChange(idx, f.key, val)}
                          />
                        ) : (
                          <input
                            type={f.type}
                            value={layer[f.key]}
                            onChange={e => handleLayerChange(idx, f.key, e.target.value)}
                            style={layerInputStyle}
                            step={f.type === 'number' ? '0.01' : undefined}
                            min={f.type === 'number' ? '0' : undefined}
                          />
                        )}
                      </td>
                    ))}
                    <td style={{ padding: '4px', textAlign: 'center', borderBottom: '1px solid #333' }}>
                      <button type="button" onClick={() => removeLayer(idx)} style={removeBtnStyle} title={t('boreholes.layers.deleteTitle')}>
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button type="button" onClick={addLayer} style={addLayerBtnStyle}>
            <FaPlus /> {t('boreholes.layers.addLayer')}
          </button>
        </Section>

        {/* ── Actions ── */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 24 }}>
          <button type="button" onClick={() => navigate('/boreholes')} style={cancelBtnStyle}>{t('boreholes.actions.cancel')}</button>
          <button type="submit" disabled={isLoading} style={{ ...saveBtnStyle, opacity: isLoading ? 0.7 : 1, cursor: isLoading ? 'not-allowed' : 'pointer' }}>
            <FaSave />
            {isLoading ? t('boreholes.actions.saving') : (isEditMode ? t('boreholes.actions.saveEdit') : t('boreholes.actions.save'))}
          </button>
        </div>
      </form>
      {showLegendLibrary && (
        <div className="modal show d-block" style={sidebarSafeOverlayStyle}>
          <div className="modal-dialog modal-xl modal-dialog-centered" style={modalDialogXlStyle}>
            <div className="modal-content" style={modalContentStyle}>
              <div className="modal-header" style={modalHeaderStyle}>
                <h5 className="modal-title">{t('boreholes.legendLibrary', { defaultValue: 'مكتبة الرموز' })}</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowLegendLibrary(false)} />
              </div>
              <div className="modal-body" style={modalBodyScrollStyle}>
                <div style={{ marginBottom: 20, paddingBottom: 12, borderBottom: '1px solid #333' }}>
                  <h6>{t('boreholes.addLegend', { defaultValue: 'إضافة رمز جديد' })}</h6>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0,1fr))', gap: 10 }}>
                    <input placeholder={t('boreholes.legends.labelEnRequired', { defaultValue: 'الاسم الإنجليزي*' })} value={newLegendForm.label_en} onChange={(e) => setNewLegendForm((p) => ({ ...p, label_en: e.target.value }))} style={layerInputStyle} />
                    <input placeholder={t('boreholes.legends.labelAr', { defaultValue: 'الاسم العربي' })} value={newLegendForm.label_ar} onChange={(e) => setNewLegendForm((p) => ({ ...p, label_ar: e.target.value }))} style={layerInputStyle} />
                    <input placeholder={t('boreholes.legends.code', { defaultValue: 'الكود' })} value={newLegendForm.code} onChange={(e) => setNewLegendForm((p) => ({ ...p, code: e.target.value }))} style={layerInputStyle} />
                    <input placeholder={t('boreholes.legends.sortOrder', { defaultValue: 'الترتيب' })} type="number" value={newLegendForm.sort_order} onChange={(e) => setNewLegendForm((p) => ({ ...p, sort_order: e.target.value }))} style={layerInputStyle} />
                    <input type="file" accept="image/*" onChange={(e) => setNewLegendForm((p) => ({ ...p, image: e.target.files?.[0] || null }))} style={layerInputStyle} />
                  </div>
                  <div style={{ marginTop: 10, display: 'flex', gap: 10 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <input type="checkbox" checked={newLegendForm.is_active} onChange={(e) => setNewLegendForm((p) => ({ ...p, is_active: e.target.checked }))} />
                      {t('common.active', { defaultValue: 'نشط' })}
                    </label>
                    <button type="button" onClick={createLegend} style={saveBtnStyle}>{t('common.save', { defaultValue: 'حفظ' })}</button>
                  </div>
                </div>
                {isLegendsLoading ? (
                  <div style={{ textAlign: 'center', padding: 20 }}>{t('loading', { defaultValue: 'جاري التحميل...' })}</div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
                    {allLegends.map((legend) => (
                      <div key={legend.id} style={{ background: '#14263b', border: '1px solid #333', borderRadius: 8, padding: 10 }}>
                        {legendImageUrl(legend) ? (
                          <img src={legendImageUrl(legend)} alt={legend.label_en || `legend-${legend.id}`} style={{ width: '100%', height: 120, objectFit: 'contain', background: '#fff', borderRadius: 6 }} />
                        ) : <div style={{ height: 120, background: '#1f2432', borderRadius: 6 }} />}
                        <div style={{ marginTop: 8, fontSize: 13 }}>{legend.label_ar || legend.label_en || legend.code || `#${legend.id}`}</div>
                        <div style={{ marginTop: 8, display: 'flex', gap: 6 }}>
                          <button
                            type="button"
                            onClick={() => updateLegend(legend.id, {
                              label_en: legend.label_en || '',
                              label_ar: legend.label_ar || '',
                              code: legend.code || '',
                              sort_order: legend.sort_order ?? 0,
                              is_active: !legend.is_active,
                              image: null,
                            })}
                            style={miniBtnStyle}
                          >
                            {legend.is_active ? t('common.disable', { defaultValue: 'تعطيل' }) : t('common.enable', { defaultValue: 'تفعيل' })}
                          </button>
                          <button
                            type="button"
                            onClick={() => setEditLegendForm({
                              id: legend.id,
                              label_en: legend.label_en || '',
                              label_ar: legend.label_ar || '',
                              code: legend.code || '',
                              sort_order: legend.sort_order ?? 0,
                              is_active: !!legend.is_active,
                              image: null,
                            })}
                            style={miniBtnStyle}
                          >
                            {t('btns.edit', { defaultValue: 'تعديل' })}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {editLegendForm && (
                <div className="modal show d-block" style={sidebarSafeOverlayStyle}>
                  <div className="modal-dialog modal-dialog-centered" style={modalDialogMdStyle}>
                    <div className="modal-content" style={modalContentStyle}>
                      <div className="modal-header" style={modalHeaderStyle}>
                        <h5 className="modal-title">{t('btns.edit', { defaultValue: 'تعديل' })}</h5>
                        <button type="button" className="btn-close btn-close-white" onClick={() => setEditLegendForm(null)} />
                      </div>
                      <div className="modal-body" style={modalBodyScrollStyle}>
                        <div style={{ display: 'grid', gap: 10 }}>
                          <input value={editLegendForm.label_en} onChange={(e) => setEditLegendForm((prev) => ({ ...prev, label_en: e.target.value }))} placeholder={t('boreholes.legends.labelEn', { defaultValue: 'الاسم الإنجليزي' })} style={layerInputStyle} />
                          <input value={editLegendForm.label_ar} onChange={(e) => setEditLegendForm((prev) => ({ ...prev, label_ar: e.target.value }))} placeholder={t('boreholes.legends.labelAr', { defaultValue: 'الاسم العربي' })} style={layerInputStyle} />
                          <input value={editLegendForm.code} onChange={(e) => setEditLegendForm((prev) => ({ ...prev, code: e.target.value }))} placeholder={t('boreholes.legends.code', { defaultValue: 'الكود' })} style={layerInputStyle} />
                          <input type="number" value={editLegendForm.sort_order} onChange={(e) => setEditLegendForm((prev) => ({ ...prev, sort_order: e.target.value }))} placeholder={t('boreholes.legends.sortOrder', { defaultValue: 'الترتيب' })} style={layerInputStyle} />
                          <input type="file" accept="image/*" onChange={(e) => setEditLegendForm((prev) => ({ ...prev, image: e.target.files?.[0] || null }))} style={layerInputStyle} />
                          <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <input type="checkbox" checked={editLegendForm.is_active} onChange={(e) => setEditLegendForm((prev) => ({ ...prev, is_active: e.target.checked }))} />
                            {t('common.active', { defaultValue: 'نشط' })}
                          </label>
                        </div>
                      </div>
                      <div className="modal-footer" style={modalFooterStyle}>
                        <button type="button" className="btn btn-secondary" onClick={() => setEditLegendForm(null)}>
                          {t('delete.cancel', { defaultValue: 'إلغاء' })}
                        </button>
                        <button
                          type="button"
                          className="btn btn-danger"
                          style={{ backgroundColor: '#AC2000', border: 'none' }}
                          onClick={() => updateLegend(editLegendForm.id, editLegendForm)}
                        >
                          {t('common.save', { defaultValue: 'حفظ' })}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {legendPickerRowIndex !== null && (
        <div className="modal show d-block" style={sidebarSafeOverlayStyle}>
          <div className="modal-dialog modal-lg modal-dialog-centered" style={modalDialogLgStyle}>
            <div className="modal-content" style={modalContentStyle}>
              <div className="modal-header" style={modalHeaderStyle}>
                <h5 className="modal-title">{t('boreholes.selectLegend', { defaultValue: 'اختر الرمز' })}</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setLegendPickerRowIndex(null)} />
              </div>
              <div className="modal-body" style={modalBodyScrollStyle}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10 }}>
                  {legends.map((legend) => (
                    <button
                      key={legend.id}
                      type="button"
                      onClick={() => {
                        handleLayerChange(legendPickerRowIndex, 'legend_id', legend.id);
                        setLegendPickerRowIndex(null);
                      }}
                      style={{ background: '#14263b', border: '1px solid #333', borderRadius: 8, color: 'white', padding: 8, textAlign: 'center' }}
                    >
                      {legendImageUrl(legend) ? (
                        <img src={legendImageUrl(legend)} alt={legend.label_en || `legend-${legend.id}`} style={{ width: '100%', height: 90, objectFit: 'contain', background: '#fff', borderRadius: 6 }} />
                      ) : <div style={{ height: 90, background: '#1f2432', borderRadius: 6 }} />}
                      <div style={{ marginTop: 6, fontSize: 12 }}>{legend.label_ar || legend.label_en || legend.code || `#${legend.id}`}</div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ─── Sub-components ─── */
const Section = ({ title, children }) => (
  <div style={{ backgroundColor: '#1b3350', border: '1px solid #333', borderRadius: 12, padding: '20px 24px', marginBottom: 20 }}>
    <h2 style={{ fontSize: 16, fontWeight: 'bold', margin: '0 0 16px 0', color: '#d0d6e0', borderBottom: '1px solid #333', paddingBottom: 10 }}>{title}</h2>
    {children}
  </div>
);

const Field = ({ label, ...rest }) => (
  <div style={{ marginBottom: 16 }}>
    <label style={labelStyle}>{label}</label>
    <input {...rest} style={inputStyle} />
  </div>
);

/* ─── Styles ─── */
const labelStyle = {
  display: 'block', marginBottom: 6, color: '#d0d6e0', fontSize: 13, fontWeight: 'bold',
};

const inputStyle = {
  width: '100%', padding: '10px 12px', backgroundColor: '#14263b',
  border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14,
  boxSizing: 'border-box',
};

const layerInputStyle = {
  width: '100%', padding: '6px 8px', backgroundColor: '#14263b',
  border: '1px solid #2a3247', borderRadius: 4, color: 'white', fontSize: 12,
  boxSizing: 'border-box',
};

const gridStyle = {
  display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '0 20px',
};

const thStyle = {
  padding: '10px 8px', textAlign: 'center', color: 'white', fontWeight: 'bold',
  fontSize: 12, borderBottom: '1px solid #333', whiteSpace: 'nowrap',
};

const saveBtnStyle = {
  backgroundColor: '#AC2000', color: 'white', border: 'none', padding: '12px 28px',
  borderRadius: 8, fontSize: 14, fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 8,
};

const cancelBtnStyle = {
  backgroundColor: '#2f3644', color: 'white', border: 'none', padding: '12px 24px',
  borderRadius: 8, fontSize: 14, fontWeight: 'bold', cursor: 'pointer',
};

const backBtnStyle = {
  backgroundColor: '#2f3644', color: 'white', border: 'none', padding: '10px 15px',
  borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
};

const addLayerBtnStyle = {
  marginTop: 12, backgroundColor: '#1a3a5c', color: 'white', border: '1px dashed #4a6fa5',
  padding: '8px 20px', borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center',
  gap: 6, fontSize: 13,
};

const removeBtnStyle = {
  backgroundColor: '#c0392b', color: 'white', border: 'none', padding: '5px 8px',
  borderRadius: 4, cursor: 'pointer', display: 'inline-flex', alignItems: 'center',
};

const secondaryBtnStyle = {
  backgroundColor: '#2f3644',
  color: 'white',
  border: '1px solid #3a4358',
  borderRadius: 6,
  padding: '8px 12px',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  cursor: 'pointer',
  fontSize: 13,
};

const miniBtnStyle = {
  backgroundColor: '#1a3a5c',
  color: 'white',
  border: 'none',
  borderRadius: 5,
  padding: '4px 8px',
  fontSize: 12,
  cursor: 'pointer',
};

const ddMenuStyle = {
  position: 'absolute', top: 'calc(100% + 2px)', left: 0, right: 0, zIndex: 50,
  backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8,
  boxShadow: '0 6px 20px rgba(0,0,0,0.5)', padding: '8px 0',
};

const ddSearchStyle = {
  width: 'calc(100% - 24px)', margin: '0 12px 8px', padding: '8px 10px',
  backgroundColor: '#14263b', border: '1px solid #333', borderRadius: 6,
  color: 'white', fontSize: 13, display: 'block',
};

const ddItemStyle = {
  padding: '9px 14px', cursor: 'pointer', fontSize: 13, color: '#d0d6e0',
  borderTop: '1px solid #2a2f3e', transition: 'background 0.15s',
};

const modalOverlayStyle = {
  position: 'fixed',
  inset: 0,
  backgroundColor: 'rgba(0,0,0,0.7)',
  zIndex: 20000,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 16,
};

const nestedModalOverlayStyle = {
  position: 'fixed',
  inset: 0,
  backgroundColor: 'rgba(0,0,0,0.55)',
  zIndex: 20010,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 16,
};

const modalDialogXlStyle = { width: '100%', maxWidth: 1100, margin: 0 };
const modalDialogLgStyle = { width: '100%', maxWidth: 900, margin: 0 };
const modalDialogMdStyle = { width: '100%', maxWidth: 560, margin: 0 };

const modalContentStyle = {
  backgroundColor: '#1b3350',
  color: 'white',
  border: '1px solid #333',
  borderRadius: 12,
  width: '100%',
  maxHeight: 'calc(100vh - 32px)',
  overflow: 'hidden',
};

const modalHeaderStyle = { borderBottom: '1px solid #333', padding: '12px 16px' };
const modalBodyScrollStyle = { overflowY: 'auto', maxHeight: 'calc(100vh - 180px)', padding: 16 };
const modalFooterStyle = { borderTop: '1px solid #333', padding: '12px 16px' };

export default AddBorehole;
